import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import {
  isPushSupported,
  getPushPermission,
  getExistingPushSubscription,
  preparePush,
  subscribeToPush,
} from '../../util/webPush';

import IconClose from '../IconClose/IconClose';

import css from './PushPromptBanner.module.css';

// Nextmine: lille "Get notified"-banner der goer push-funktionen synlig dér,
// hvor notifikationerne faktisk lander (indbakken). Vises kun naar enheden
// understoetter push, tilladelsen ikke er afvist, og enheden ikke allerede er
// tilmeldt. "Not now" gemmer banneret vaek i en periode via localStorage.
const DISMISS_KEY = 'nextmine_push_prompt_dismissed';
const DISMISS_DAYS = 30;

const isDismissed = () => {
  try {
    const ts = window.localStorage.getItem(DISMISS_KEY);
    return !!ts && Date.now() - Number(ts) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch (e) {
    return false;
  }
};

const rememberDismiss = () => {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch (e) {
    // Privat vindue el.lign. - banneret kommer saa bare igen naeste besoeg.
  }
};

const PushPromptBanner = props => {
  // headingId/textId lader hver side saette sin egen, konkrete grund til at
  // sige ja (svar paa beskeder, bud paa egne annoncer, ...).
  const { className, headingId, textId } = props;
  const [status, setStatus] = useState('hidden'); // hidden|off|busy|on|error
  const [prepared, setPrepared] = useState(null);

  useEffect(() => {
    if (!isPushSupported() || getPushPermission() === 'denied' || isDismissed()) {
      return;
    }
    let cancelled = false;
    getExistingPushSubscription()
      .then(sub => {
        if (cancelled || sub) return;
        setStatus('off');
        // Noegle + SW klargoeres foer klikket - paa iOS skal subscribe ske i
        // selve fingertrykket, saa der maa ikke ventes paa netvaerk dér.
        return preparePush().then(p => {
          if (!cancelled) setPrepared(p);
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleEnable = () => {
    setStatus('busy');
    subscribeToPush(prepared)
      .then(() => setStatus('on'))
      .catch(() => {
        if (getPushPermission() === 'denied') {
          setStatus('hidden');
        } else {
          setStatus('error');
        }
      });
  };

  const handleDismiss = () => {
    rememberDismiss();
    setStatus('hidden');
  };

  if (status === 'hidden') {
    return null;
  }

  return (
    <div className={classNames(css.root, className)}>
      {status === 'on' ? (
        <p className={css.text}>
          <FormattedMessage id="PushPromptBanner.enabled" />
        </p>
      ) : (
        <>
          <div className={css.textWrapper}>
            <p className={css.heading}>
              <FormattedMessage id={headingId || 'PushPromptBanner.heading'} />
            </p>
            <p className={css.text}>
              <FormattedMessage
                id={
                  status === 'error'
                    ? 'PushPromptBanner.error'
                    : textId || 'PushPromptBanner.text'
                }
              />
            </p>
          </div>
          <div className={css.actions}>
            <button
              className={css.enableButton}
              type="button"
              onClick={handleEnable}
              disabled={status === 'busy'}
            >
              <FormattedMessage
                id={status === 'busy' ? 'PushPromptBanner.enabling' : 'PushPromptBanner.enable'}
              />
            </button>
            <button
              className={css.dismissButton}
              type="button"
              onClick={handleDismiss}
              title="Not now"
            >
              <IconClose size="small" className={css.dismissIcon} />
              <span className={css.dismissLabel}>
                <FormattedMessage id="PushPromptBanner.dismiss" />
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PushPromptBanner;
